import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Spinner, Nav, Table } from 'react-bootstrap';
import { getMyFastrackNodes } from '../../api/fastrackApi';
import { fmtIST } from '../../utils/dateFormat';
import { ActiveNodeCard } from '../../components/fastrack/ActiveNodeCard';

const isImpersonating = () => {
  try { return !!JSON.parse(localStorage.getItem('impersonating')); } catch { return false; }
};

const accent = '#cc66ff';
const cardDark = { background: 'linear-gradient(135deg, #0d0d0d 0%, #1a0033 100%)', color: '#fff', border: '1px solid #9933ff44' };

const NodeTimelineItem = ({ node, index }) => {
  const isComplete = node.status === 'complete';
  return (
    <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
      {/* Spine */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 32 }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: isComplete ? '#9933ff' : '#1a6600',
          border: `2px solid ${isComplete ? accent : '#66ff99'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0,
        }}>
          {index + 1}
        </div>
        <div style={{ flex: 1, width: 2, background: '#333', minHeight: 8 }} />
      </div>

      {/* Content */}
      <Card style={{ ...cardDark, flex: 1, marginBottom: 0 }}>
        <Card.Body className="py-2 px-3">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <Badge bg={isComplete ? 'secondary' : 'success'} className="me-2">
                {isComplete ? 'Complete' : 'Active'}
              </Badge>
              {node.position && (
                <span style={{ color: '#aaa', fontSize: 12 }}>
                  {node.position === 'left' ? 'Left' : 'Right'} child
                  {node.parentId ? '' : ' (Root)'}
                </span>
              )}
              {!node.parentId && <span style={{ color: '#aaa', fontSize: 12 }}>Root position</span>}
            </div>
            <div style={{ fontSize: 11, color: '#777' }}>
              Placed: {fmtIST(node.createdAt)}
            </div>
          </div>

          <div className="d-flex gap-4 mt-2 flex-wrap">
            <div style={{ fontSize: 12 }}>
              <span style={{ color: '#aaa' }}>Left: </span>
              {node.leftChildId
                ? <span style={{ color: accent }}>{node.leftChildId.userId?.name} ({node.leftChildId.userId?.userCode})</span>
                : <span style={{ color: '#555' }}>Empty</span>}
            </div>
            <div style={{ fontSize: 12 }}>
              <span style={{ color: '#aaa' }}>Right: </span>
              {node.rightChildId
                ? <span style={{ color: accent }}>{node.rightChildId.userId?.name} ({node.rightChildId.userId?.userCode})</span>
                : <span style={{ color: '#555' }}>Empty</span>}
            </div>
          </div>

          {isComplete && (
            <div className="mt-2 d-flex gap-4 flex-wrap" style={{ borderTop: '1px solid #333', paddingTop: 8 }}>
              <div style={{ fontSize: 12 }}>
                <span style={{ color: '#aaa' }}>Bonus earned: </span>
                <strong style={{ color: '#66ff99' }}>{node.bonusAmount} USDT</strong>
              </div>
              <div style={{ fontSize: 12 }}>
                <span style={{ color: '#aaa' }}>Completed: </span>
                <span style={{ color: '#fff' }}>{fmtIST(node.completedAt)}</span>
              </div>
              {node.rebirthToNodeId && (
                <div style={{ fontSize: 12 }}>
                  <span style={{ color: '#aaa' }}>Reborn: </span>
                  <span style={{ color: '#66aaff' }}>{fmtIST(node.rebirthToNodeId.createdAt)}</span>
                </div>
              )}
            </div>
          )}

          {node.rebirthFromNodeId && (
            <div style={{ fontSize: 11, color: '#9933ff', marginTop: 6 }}>
              Reborn from Node #{index} — completed {fmtIST(node.rebirthFromNodeId.completedAt)}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

const ConditionBanner = ({ currentBatch, directSlotCount, unlockedBatches, conditionMet, heldAmount, planName }) => {
  const isFree = currentBatch === 1;
  const batchStart = (currentBatch - 1) * 8 + 1;
  const batchEnd   = currentBatch * 8;
  const needed     = currentBatch - 1;

  return (
    <div style={{
      borderRadius: 12,
      border: `1px solid ${conditionMet ? 'rgba(102,255,153,0.25)' : 'rgba(255,170,68,0.25)'}`,
      background: conditionMet ? 'rgba(102,255,153,0.05)' : 'rgba(255,170,68,0.05)',
      padding: '16px 20px',
      marginBottom: 20,
      display: 'flex',
      flexWrap: 'wrap',
      gap: 16,
      alignItems: 'center',
      justifyContent: 'space-between',
    }}>
      <div>
        <div style={{ color: '#555', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
          Current Batch — Cycles {batchStart}–{batchEnd}
        </div>
        {isFree ? (
          <div style={{ color: '#66ff99', fontWeight: 600, fontSize: 14 }}>
            ✓ Free batch — no conditions required
          </div>
        ) : conditionMet ? (
          <div style={{ color: '#66ff99', fontWeight: 600, fontSize: 14 }}>
            ✓ Condition met — {directSlotCount}/{needed} direct referral{needed > 1 ? 's' : ''} in {planName}
          </div>
        ) : (
          <div>
            <div style={{ color: '#ffaa44', fontWeight: 600, fontSize: 14 }}>
              ○ Condition not met — need {needed} direct referral{needed > 1 ? 's' : ''} in {planName}
            </div>
            <div style={{ color: '#888', fontSize: 12, marginTop: 2 }}>
              {directSlotCount} of {needed} direct referral{needed > 1 ? 's' : ''} have purchased this slot
            </div>
          </div>
        )}
      </div>

      {heldAmount > 0 && (
        <div style={{
          background: 'rgba(255,170,68,0.1)',
          border: '1px solid rgba(255,170,68,0.3)',
          borderRadius: 8,
          padding: '10px 16px',
          textAlign: 'center',
          minWidth: 130,
        }}>
          <div style={{ color: '#555', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>On Hold</div>
          <div style={{ color: '#ffaa44', fontSize: 20, fontWeight: 800, marginTop: 2 }}>{heldAmount.toFixed(2)}</div>
          <div style={{ color: '#888', fontSize: 10 }}>USDT — releases on condition met</div>
        </div>
      )}
    </div>
  );
};

const PlanPanel = ({ planData }) => {
  const {
    plan, nodes,
    completedCycles = 0, directSlotCount = 0, unlockedBatches = 1,
    currentBatch = 1, conditionMet = true, heldAmount = 0,
  } = planData;
  const completedNodes = nodes.filter((n) => n.status === 'complete');
  const activeNode     = nodes.find((n) => n.status === 'active');
  const creditedTotal  = completedNodes
    .filter((n) => n.bonusStatus === 'credited')
    .reduce((s, n) => s + (n.bonusAmount || 0), 0);

  return (
    <div>
      {/* Condition banner, plan stats, active node & rebirth chain — admin-impersonation view only */}
      {isImpersonating() && (
        <>
          <ConditionBanner
            currentBatch={currentBatch}
            directSlotCount={directSlotCount}
            unlockedBatches={unlockedBatches}
            conditionMet={conditionMet}
            heldAmount={heldAmount}
            planName={plan.planName}
          />

          <Row className="g-3 mb-4">
            <Col xs={6} md={3}>
              <Card style={cardDark} className="text-center p-3">
                <div style={{ fontSize: 22, fontWeight: 700, color: '#66ff99' }}>{creditedTotal.toFixed(2)}</div>
                <div style={{ fontSize: 12, color: '#aaa' }}>Credited (USDT)</div>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card style={cardDark} className="text-center p-3">
                <div style={{ fontSize: 22, fontWeight: 700, color: '#ffaa44' }}>{heldAmount.toFixed(2)}</div>
                <div style={{ fontSize: 12, color: '#aaa' }}>On Hold (USDT)</div>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card style={cardDark} className="text-center p-3">
                <div style={{ fontSize: 22, fontWeight: 700, color: accent }}>{completedCycles}</div>
                <div style={{ fontSize: 12, color: '#aaa' }}>Cycles Completed</div>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card style={cardDark} className="text-center p-3">
                <div style={{ fontSize: 22, fontWeight: 700, color: activeNode ? '#66ff99' : '#555' }}>
                  {activeNode ? 'Active' : 'None'}
                </div>
                <div style={{ fontSize: 12, color: '#aaa' }}>Current Node</div>
              </Card>
            </Col>
          </Row>

          {activeNode && (
            <>
              <h6 style={{ color: '#aaa', marginBottom: 12 }}>Current Active Node</h6>
              <ActiveNodeCard node={{ ...activeNode, planId: plan }} />
            </>
          )}

          <h6 style={{ color: '#aaa', margin: '24px 0 12px' }}>Full Node History (Rebirth Chain)</h6>
          {nodes.length === 0 ? (
            <div style={{ color: '#555', fontSize: 13 }}>No nodes yet.</div>
          ) : (
            nodes.map((node, i) => <NodeTimelineItem key={node._id} node={node} index={i} />)
          )}
        </>
      )}

      {/* Bonus log table */}
      {completedNodes.length > 0 && (
        <>
          <h6 style={{ color: '#aaa', margin: '24px 0 12px' }}>Bonus Transaction Log</h6>
          <Card style={cardDark}>
            <Table responsive className="mb-0" style={{ color: '#ddd' }}>
              <thead style={{ borderBottom: '1px solid #9933ff44' }}>
                <tr>
                  <th style={{ color: '#aaa', fontWeight: 500, fontSize: 13 }}>#</th>
                  <th style={{ color: '#aaa', fontWeight: 500, fontSize: 13 }}>Completed</th>
                  <th style={{ color: '#aaa', fontWeight: 500, fontSize: 13 }}>Batch</th>
                  <th style={{ color: '#aaa', fontWeight: 500, fontSize: 13 }}>Bonus (20%)</th>
                  <th style={{ color: '#aaa', fontWeight: 500, fontSize: 13 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {completedNodes.map((node, i) => (
                  <tr key={node._id} style={{ borderBottom: '1px solid #9933ff22' }}>
                    <td style={{ fontSize: 13 }}>{node.cycleNumber ?? i + 1}</td>
                    <td style={{ fontSize: 13 }}>{fmtIST(node.completedAt)}</td>
                    <td style={{ fontSize: 13, color: '#aaa' }}>
                      {node.batchNumber ? `Batch ${node.batchNumber}` : '—'}
                    </td>
                    <td style={{ fontSize: 13, color: '#66ff99', fontWeight: 600 }}>{node.bonusAmount} USDT</td>
                    <td style={{ fontSize: 13 }}>
                      {node.bonusStatus === 'credited' && (
                        <span style={{ color: '#66ff99', fontWeight: 600 }}>✓ Credited</span>
                      )}
                      {node.bonusStatus === 'held' && (
                        <span style={{ color: '#ffaa44', fontWeight: 600 }}>⏸ On Hold</span>
                      )}
                      {node.bonusStatus === 'pending' && (
                        <span style={{ color: '#555' }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
};

const Fastrack = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePlanKey, setActivePlanKey] = useState(null);

  useEffect(() => {
    getMyFastrackNodes()
      .then((res) => {
        setData(res.data);
        if (res.data.plans?.length > 0) {
          setActivePlanKey(res.data.plans[0].plan._id);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" style={{ color: accent }} />
      </Container>
    );
  }

  const hasPlans = data?.plans?.length > 0;

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 40 }}>
      <Container className="pt-4">
        {/* Header */}
        <div className="mb-4">
          <h4 style={{ color: '#fff', fontWeight: 700 }}>
            Global Fastrack Bonus
          </h4>
          <div style={{ color: '#aaa', fontSize: 13 }}>
            Global fastrack income — earn 20% when your Cycle completes, then rebirth and repeat
          </div>
        </div>

        {/* Global summary */}
        {data?.summary && (
          <Row className="g-3 mb-4">
            <Col xs={6} md={3}>
              <Card style={{ ...cardDark, borderColor: '#66ff9944' }} className="text-center p-3">
                <div style={{ fontSize: 24, fontWeight: 800, color: '#66ff99' }}>
                  {data.summary.totalBonusEarned.toFixed(2)}
                </div>
                <div style={{ color: '#aaa', fontSize: 12 }}>Credited (USDT)</div>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card style={{ ...cardDark, borderColor: '#ffaa4444' }} className="text-center p-3">
                <div style={{ fontSize: 24, fontWeight: 800, color: '#ffaa44' }}>
                  {data.summary.totalHeld.toFixed(2)}
                </div>
                <div style={{ color: '#aaa', fontSize: 12 }}>On Hold (USDT)</div>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card style={cardDark} className="text-center p-3">
                <div style={{ fontSize: 24, fontWeight: 800, color: accent }}>
                  {data.summary.totalCompleted}
                </div>
                <div style={{ color: '#aaa', fontSize: 12 }}>Cycles Completed</div>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card style={cardDark} className="text-center p-3">
                <div style={{ fontSize: 24, fontWeight: 800, color: '#66aaff' }}>
                  {data.summary.totalActive}
                </div>
                <div style={{ color: '#aaa', fontSize: 12 }}>Active Nodes</div>
              </Card>
            </Col>
          </Row>
        )}

        {!hasPlans ? (
          <Card style={cardDark} className="text-center p-5">
            <div style={{ color: '#555', fontSize: 14 }}>
              You have not subscribed to any plan yet.<br />
              Subscribe to a plan to enter the Global Fastrack network.
            </div>
          </Card>
        ) : (
          <>
            {/* Plan tabs */}
            {data.plans.length > 1 && (
              <Nav variant="tabs" className="mb-4" style={{ borderBottom: '1px solid #9933ff44', overflowX: 'auto', overflowY: 'hidden', flexWrap: 'nowrap', WebkitOverflowScrolling: 'touch' }}>
                {data.plans.map(({ plan }) => (
                  <Nav.Item key={plan._id}>
                    <Nav.Link
                      active={activePlanKey === plan._id}
                      onClick={() => setActivePlanKey(plan._id)}
                      style={{
                        color: activePlanKey === plan._id ? accent : '#aaa',
                        background: activePlanKey === plan._id ? '#1a003388' : 'transparent',
                        border: activePlanKey === plan._id ? `1px solid ${accent}` : '1px solid transparent',
                        borderBottom: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      {plan.planName} — {plan.amount} USDT
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            )}

            {data.plans
              .filter((pd) => data.plans.length === 1 || activePlanKey === pd.plan._id)
              .map((pd) => (
                <PlanPanel key={pd.plan._id} planData={pd} />
              ))}
          </>
        )}
      </Container>
    </div>
  );
};

export default Fastrack;
